import { useMemo } from 'react';
import { isObject } from 'lodash-es';
import { useDevice } from 'seagull';
import { billingInitiationTextsPath, billingStagesTextsPath, billingTriggersTextsPath, makeInitialOneTimeInStages, serviceBillingStagesTermsOptions, serviceBillingTriggerOptions } from '~/business-logic/billingLogic';
import { recurrencePeriodsTextsPath } from '~/constants';
import { useIoContext } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { serviceBillingModel, serviceCostModel, serviceModel } from '~/models';
import { OneTimeInStagesError, validateOneTimeInStages } from '~/models/service/logic';
import { isAwaitingApproval, isFromAmendment } from '~/models/service/selectors';
import { resolveStageScheduleContext } from '~/models/service/stageScheduleContext';
import { makeBillUpfrontState } from '~/models/service-billing';
import { PreapprovalPeriodType, RecurrencePeriodTypes } from '~/models/service-billing/model';
import { fromDateStampObject } from '~/models/time/translator';
import { GetBillingInitiationControlTID } from './BillingInitiationControl';
function matchOption(option, matcher) {
    switch(option){
        case 'manualFixed':
        case 'manualVariable':
            return matcher.manual();
        case 'oneTimeInStages':
            return matcher.inStages();
        case 'approval':
        case 'ongoing':
            return matcher.auto();
        default:
            {
                const _exhaustive = option;
                throw new Error("Unhandled trigger option: ".concat(_exhaustive));
            }
    }
}
function serviceBillingOccurrenceOptions(param) {
    let { isVariablePrice, recurrencePeriodsTexts } = param;
    return [
        {
            value: 'onDemand',
            disabled: !isVariablePrice
        },
        {
            value: 'onetime'
        },
        {
            value: 'inStages'
        },
        {
            value: RecurrencePeriodTypes.Weekly
        },
        {
            value: RecurrencePeriodTypes.BiWeekly
        },
        {
            value: RecurrencePeriodTypes.Monthly
        },
        {
            value: RecurrencePeriodTypes.Quarterly
        },
        {
            value: RecurrencePeriodTypes.Yearly
        }
    ].map((param)=>{
        let { value, disabled } = param;
        return {
            value,
            text: serviceBillingModel.extendedRecurrencePeriodText(value, false, recurrencePeriodsTexts),
            disabled
        };
    });
}
function getOccurrenceValue(service) {
    const recurrencePeriod = serviceBillingModel.recurrencePeriod(service.billing);
    const oneTimeInStages = serviceBillingModel.oneTimeInStages(service.billing);
    if (oneTimeInStages) return 'inStages';
    if (serviceBillingModel.isOnDemand(service.billing)) return 'onDemand';
    if (!recurrencePeriod) return 'onetime';
    return recurrencePeriod;
}
function getTriggerValue(service) {
    const manualOption = ()=>serviceCostModel.isFixedPrice(service.cost) ? 'manualFixed' : 'manualVariable';
    return serviceBillingModel.accept(service.billing, {
        visitOneTimeManual: manualOption,
        visitOnApproval: ()=>'approval',
        visitOneTimeInStages: ()=>'oneTimeInStages',
        visitOngoing: ()=>'ongoing',
        visitRepeatableManual: manualOption,
        visitOnDemand: manualOption
    });
}
export function getOccurrenceLabel(occurrence, t, recurrencePeriodsTexts) {
    if (!occurrence) return t.labels.once();
    return serviceBillingModel.extendedRecurrencePeriodText(occurrence, true, recurrencePeriodsTexts).toLowerCase();
}
export function getTriggerLabel(option, t) {
    const autoLabel = t.labels.automatically();
    const manualLabel = t.labels.manually();
    const optionLabelMap = {
        approval: autoLabel,
        ongoing: autoLabel,
        manualVariable: manualLabel,
        manualFixed: manualLabel,
        oneTimeInStages: t.labels.inStages(),
        onDemand: manualLabel
    };
    return optionLabelMap[option];
}
export function getOccurrenceOptionTooltip(param) {
    let { isInStagesOption, isOnDemandOption, isDisabled, hasValueDiscount, isFixedPrice, oneTimeInStages, t } = param;
    if (!isDisabled) return undefined;
    if (isInStagesOption && hasValueDiscount && !oneTimeInStages) return t.tooltips.mustBePercentage();
    if (isInStagesOption && !isFixedPrice && !oneTimeInStages) return t.tooltips.inStagesFixedPriceOnly();
    if (isOnDemandOption) return t.tooltips.onDemandVariablePriceOnly();
    return undefined;
}
export function useBillingInitiationControl(args) {
    var _ref;
    const { service, agreementData, isExistingService, disabled, testId, onChange } = args;
    const testIds = useMemo(()=>GetBillingInitiationControlTID(testId), [
        testId
    ]);
    const { BillingInitiationControlTestIds } = testIds;
    const { isMobileDevice } = useDevice();
    const isDisabled = !!disabled;
    const disabledReason = isObject(disabled) ? disabled.reason : undefined;
    const featureFlags = useFeatureFlags();
    const { getNow } = useIoContext();
    const { t: billingTriggersTexts } = useLocaleKeys(billingTriggersTextsPath);
    const { t: billingStagesTexts } = useLocaleKeys(billingStagesTextsPath);
    const { t: billingInitiationTexts } = useLocaleKeys(billingInitiationTextsPath);
    const { t: recurrencePeriodsTexts } = useLocaleKeys(recurrencePeriodsTextsPath);
    const now = useMemo(()=>getNow(), []);
    const isFromAgreement = agreementData.isActiveAgreement();
    const occurrenceValue = getOccurrenceValue(service);
    const triggerValue = getTriggerValue(service);
    const isOngoing = serviceBillingModel.isOngoing(service.billing);
    const oneTimeInStages = serviceBillingModel.oneTimeInStages(service.billing);
    const isYearly = serviceBillingModel.recurrencePeriod(service.billing) === RecurrencePeriodTypes.Yearly;
    const isAutomaticOptionSelected = serviceBillingModel.isAutomatic(service.billing);
    const shouldShowYearlyManualTooltip = isYearly && !featureFlags.YEARLY_AUTOMATIC_SERVICE && !serviceBillingModel.isManual(service.billing);
    const hasNonPercentageDiscountError = oneTimeInStages ? validateOneTimeInStages(service).includes(OneTimeInStagesError.NonPercentageDiscount) : false;
    const discount = serviceModel.discount(service);
    const hasValueDiscount = (_ref = discount === null || discount === void 0 ? void 0 : discount.isValue()) !== null && _ref !== void 0 ? _ref : false;
    const isFixedPrice = serviceCostModel.isFixedPrice(service.cost);
    const shouldDisableInStagesOption = (!isFixedPrice || hasValueDiscount) && !oneTimeInStages;
    const termsOptions = serviceBillingStagesTermsOptions({
        t: billingStagesTexts,
        now,
        agreementEffectiveDate: agreementData.effectiveDate,
        isFreshlyAddedToApprovedAgreement: isFromAgreement && !isExistingService
    });
    const stageScheduleContext = resolveStageScheduleContext({
        isTemplate: false,
        isAmendment: isFromAmendment(service),
        effectiveDate: agreementData.effectiveDate ? fromDateStampObject(agreementData.effectiveDate) : null,
        now
    });
    const isVariablePrice = serviceCostModel.isVariablePrice(service.cost);
    const occurrenceOptions = serviceBillingOccurrenceOptions({
        isVariablePrice,
        recurrencePeriodsTexts
    });
    const triggerOptions = serviceBillingTriggerOptions({
        t: billingTriggersTexts,
        recurrencePeriod: serviceBillingModel.recurrencePeriod(service.billing),
        pricingType: service.cost.pricingType,
        billingTrigger: service.billing.trigger,
        isFromAgreement,
        isExistingService,
        featureFlags,
        withEffectiveDate: {
            effectiveDate: agreementData.effectiveDate,
            now
        }
    });
    function handleOccurrenceChange(event) {
        const newOccurrence = event.target.value;
        const currentRecurrencePeriod = serviceBillingModel.recurrencePeriod(service.billing);
        const isRecurring = !!currentRecurrencePeriod;
        if (newOccurrence === 'onetime') {
            if (isRecurring || oneTimeInStages || serviceBillingModel.isOnDemand(service.billing)) {
                serviceBillingModel.accept(service.billing, {
                    visitOneTimeManual: ()=>{},
                    visitOnApproval: ()=>{},
                    visitOneTimeInStages: ()=>onChange(serviceModel.setOneTimeManual(service)),
                    visitOngoing: ()=>onChange(serviceModel.setOnApproval(service)),
                    visitRepeatableManual: ()=>onChange(serviceModel.setOneTimeManual(service)),
                    visitOnDemand: ()=>onChange(serviceModel.setOneTimeManual(service))
                });
            }
        } else if (newOccurrence === 'onDemand') {
            const existingMaxCharges = serviceBillingModel.maxCharges(service.billing);
            const existingPeriod = serviceBillingModel.preapprovalPeriod(service.billing);
            onChange(serviceModel.setBillingTrigger(service, serviceBillingModel.makeOnDemandBilling(existingPeriod !== null && existingPeriod !== void 0 ? existingPeriod : PreapprovalPeriodType.Monthly, {
                maxCharges: existingMaxCharges
            })));
        } else if (newOccurrence === 'inStages') {
            if (!isFixedPrice || hasValueDiscount) return;
            onChange(serviceModel.setOneTimeInStages(service, makeInitialOneTimeInStages()));
        } else {
            const newRecurrencePeriod = newOccurrence;
            serviceBillingModel.accept(service.billing, {
                visitOneTimeManual: ()=>onChange(serviceModel.setBillingTrigger(service, serviceBillingModel.makeRepeatableManualBilling(newRecurrencePeriod, {}))),
                visitOnApproval: ()=>onChange(serviceModel.setBillingTrigger(service, serviceBillingModel.makeOngoingBilling(newRecurrencePeriod, {
                        isAwaitingApproval: isAwaitingApproval(service.status)
                    }))),
                visitOneTimeInStages: ()=>onChange(serviceModel.setBillingTrigger(service, serviceBillingModel.makeRepeatableManualBilling(newRecurrencePeriod, {}))),
                visitOngoing: ()=>onChange(serviceModel.setBillingTrigger(service, serviceBillingModel.makeOngoingBilling(newRecurrencePeriod, {
                        isAwaitingApproval: isAwaitingApproval(service.status),
                        billingDayOfMonth: serviceBillingModel.billingDayOfMonth(service.billing),
                        billingDayOfWeek: serviceBillingModel.billingDayOfWeek(service.billing),
                        billingDayOfYear: serviceBillingModel.billingDayOfYear(service.billing),
                        isBilledUpfront: serviceBillingModel.isBilledUpfront(service.billing),
                        maxCharges: serviceBillingModel.maxCharges(service.billing),
                        billUpfrontState: makeBillUpfrontState(serviceBillingModel.isSkipBillingOnAcceptance(service.billing))
                    }))),
                visitRepeatableManual: ()=>onChange(serviceModel.setBillingTrigger(service, serviceBillingModel.makeRepeatableManualBilling(newRecurrencePeriod, {
                        maxCharges: serviceBillingModel.maxCharges(service.billing)
                    }))),
                visitOnDemand: ()=>onChange(serviceModel.setBillingTrigger(service, serviceBillingModel.makeRepeatableManualBilling(newRecurrencePeriod, {
                        maxCharges: serviceBillingModel.maxCharges(service.billing)
                    })))
            });
        }
    }
    function handleTriggerChange(event) {
        const option = event.target.value;
        const setInStages = ()=>onChange(serviceModel.setOneTimeInStages(service, makeInitialOneTimeInStages()));
        const setOnApproval = ()=>onChange(serviceModel.setOnApproval(service));
        const setOneTimeManual = ()=>onChange(serviceModel.setOneTimeManual(service));
        const doNothing = ()=>{};
        serviceBillingModel.accept(service.billing, {
            visitOneTimeManual: ()=>matchOption(option, {
                    manual: doNothing,
                    auto: setOnApproval,
                    inStages: setInStages
                }),
            visitOnApproval: ()=>matchOption(option, {
                    manual: setOneTimeManual,
                    auto: doNothing,
                    inStages: setInStages
                }),
            visitOneTimeInStages: ()=>matchOption(option, {
                    manual: setOneTimeManual,
                    auto: setOnApproval,
                    inStages: doNothing
                }),
            visitRepeatableManual: ()=>matchOption(option, {
                    manual: doNothing,
                    auto: ()=>onChange(serviceModel.setOngoing(service)),
                    inStages: doNothing
                }),
            visitOngoing: ()=>matchOption(option, {
                    manual: ()=>onChange(serviceModel.setRepeatableManual(service)),
                    auto: doNothing,
                    inStages: doNothing
                }),
            visitOnDemand: doNothing
        });
    }
    function handleTermsChange(event) {
        if (!oneTimeInStages) return;
        const terms = event.target.value;
        onChange(serviceModel.setOneTimeInStages(service, oneTimeInStages.setTerms(terms)));
    }
    return {
        BillingInitiationControlTestIds,
        isMobileDevice,
        isDisabled,
        disabledReason,
        featureFlags,
        billingTriggersTexts,
        billingInitiationTexts,
        occurrenceValue,
        triggerValue,
        oneTimeInStages,
        isOngoing,
        isYearly,
        isAutomaticOptionSelected,
        shouldShowYearlyManualTooltip,
        hasNonPercentageDiscountError,
        hasValueDiscount,
        isFixedPrice,
        shouldDisableInStagesOption,
        termsOptions,
        recurrencePeriodsTexts,
        occurrenceOptions,
        triggerOptions,
        stageScheduleContext,
        handleOccurrenceChange,
        handleTriggerChange,
        handleTermsChange
    };
}
